import { Link as RemixLink } from "@remix-run/react";

import {
  Layout,
  Header,
  Content,
} from "#app/components/layouts/MarketingPage.jsx";
import { CallToAction } from "#app/components/index.jsx";

import { FAQ, PRICING_FAQ_ANSWER } from "#app/components/faq/faq.jsx";
import { RESERVATION_SYSTEM_COUNT } from "#app/config/countries.js";
import { getSeoMeta, getBaseUrl, getDefaultOgImage } from "#app/utils/meta.js";
import { getFaqPageSchema } from "#app/utils/structuredData.js";

const FAQ_QUESTIONS = [
  {
    question: "What is Schnerp?",
    answer:
      "We help you find available sites at sold out campgrounds. With parks opening for reservations months in advance, and popular campgrounds selling out within minutes on opening day, we discovered that the best way to get a site is when someone's plans change and they cancel their reservation.",
  },
  {
    question: "How does it work?",
    answer:
      "First, you create a request, telling us what campground you're watching, your arrival dates, and your preferred campsites. Then, our system takes over and checks your selected campground for a cancellation. If one of the sites in your availability window becomes available, we send you an email (or a text) with a link to book the spot.",
  },
  {
    question: 'So why "Schnerp"?',
    answer:
      "It's a word my friend group uses when talking about hunting powder on a ski resort a few days after a storm. In that context, it vaguely means to acquire a scarce resource. Also, it's really fun to say.",
  },
  {
    question: "Do you book a site for me?",
    answer:
      "No. We only monitor the availability of sites in campgrounds and notify you of an opening. It's up to you to research which one is right for you, and make the booking via the park's reservations system.",
  },
  {
    question: "What campgrounds can I watch?",
    answer: `We watch campgrounds across ${RESERVATION_SYSTEM_COUNT} booking systems in Canada, the United States, Australia, and New Zealand — everything from national and state parks to holiday parks and backcountry huts. See the full list of booking systems we watch on the Create a New Request page, organized by country. We're always expanding to new parks and booking systems.`,
  },
  {
    question: "How much does it cost?",
    answer: PRICING_FAQ_ANSWER,
  },
  {
    question: "How often do you check a site?",
    answer:
      "We check most parks every 1-5 minutes, but it's not a fixed rate. The more popular a park is, the faster we check it.",
  },
  {
    question: "How can I watch for multiple sites?",
    answer:
      "When creating a request, look for the Double Site or Party Size filters if you need adjacent or group sites. You can also create multiple requests to watch different campgrounds or date ranges at the same time.",
  },
  {
    question: "Why am I not seeing some campsites listed?",
    answer:
      "For the campgrounds that we do support, we run an automated process to collect a campsite list. This process isn't perfect and occasionally, we miss some. If you find a mistake, email us at help@schnerp.com.",
  },
  {
    question: "Don't some parks already offer notifications?",
    answer:
      "A few agencies like Parks Canada and BC Parks have added basic notification features. Schnerp goes further with more notification channels (email, text, WhatsApp, push), flexible date ranges, refined filters, priority notifications, and works across dozens of booking systems in four countries.",
  },
  {
    question: "Does your service slow down park websites?",
    answer:
      "No, we feel very strongly about not abusing park websites. Our service is designed to combine checks from multiple requests into a single query, which is more efficient than if every person manually refreshed the page themselves.",
  },
];

export function meta({ matches }) {
  const baseUrl = getBaseUrl(matches);
  return getSeoMeta({
    title: "FAQs | Schnerp",
    description:
      "Schnerp is not a normal booking platform. Here, we'll explain what we're about and how we can help you book sold-out campgrounds.",
    url: `${baseUrl}/faq`,
    image: getDefaultOgImage(baseUrl),
  });
}

export default function FAQPage() {
  const faqSchema = getFaqPageSchema(FAQ_QUESTIONS);

  return (
    <Layout>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Header
        title="Your questions, answered"
        subtitle="Schnerp is not your normal booking platform. Let us explain."
      />
      <Content>
        <FAQ />
        <CallToAction
          heading="Get back to the things that matter"
          body="Tell us what campground you're watching and let us take over the search."
          buttonText="Create a request"
          buttonProps={{
            as: RemixLink,
            to: "/new",
          }}
        />
      </Content>
    </Layout>
  );
}
